import React, { useState, useEffect, useRef } from 'react';

const StartMenu = ({ launchApp, toggleTheme, currentTheme, closeMenu }) => {
  const menuRef = useRef(null);
  const [activeCategory, setActiveCategory] = useState('pinned');
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  
  // Handle clicks outside the menu to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          !event.target.closest('.start-button')) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeMenu]);

  // Windows 12 style categories
  const categories = [
    { id: 'pinned', label: 'Pinned', icon: 'thumbtack' },
    { id: 'recent', label: 'Recent', icon: 'clock' },
    { id: 'all', label: 'All apps', icon: 'th-large' },
    { id: 'recommended', label: 'Recommended', icon: 'star' }
  ];

  // Apps section data
  const appSections = [
    {
      title: 'Pinned',
      apps: [
        { id: 'file-explorer', name: 'File Explorer', icon: 'folder', component: 'FileExplorer' },
        { id: 'web-browser', name: 'Web Browser', icon: 'globe', component: 'WebBrowser', url: 'duckduckgo://search' },
        { id: 'kiwi-ai', name: 'Kiwi AI', icon: 'robot', component: 'AIAssistant' },
        { id: 'terminal', name: 'Terminal', icon: 'terminal', component: 'Terminal' },
        { id: 'store', name: 'App Store', icon: 'store', component: 'Store' }
      ]
    },
    {
      title: 'Recent',
      apps: [
        { id: 'terminal', name: 'Terminal', icon: 'terminal', component: 'Terminal' },
        { id: 'kiwi-ai', name: 'Kiwi AI', icon: 'robot', component: 'AIAssistant' },
        { id: 'file-explorer', name: 'File Explorer', icon: 'folder', component: 'FileExplorer' },
        { id: 'web-browser', name: 'Web Browser', icon: 'globe', component: 'WebBrowser', url: 'duckduckgo://search' }
      ]
    },
    {
      title: 'All apps',
      apps: [
        { id: 'file-explorer', name: 'File Explorer', icon: 'folder', component: 'FileExplorer' },
        { id: 'kiwi-ai', name: 'Kiwi AI', icon: 'robot', component: 'AIAssistant' },
        { id: 'settings', name: 'Settings', icon: 'cog', component: 'Settings' },
        { id: 'store', name: 'App Store', icon: 'store', component: 'Store' },
        { id: 'terminal', name: 'Terminal', icon: 'terminal', component: 'Terminal' },
        { id: 'web-browser', name: 'Web Browser', icon: 'globe', component: 'WebBrowser', url: 'duckduckgo://search' }
      ]
    },
    {
      title: 'Recommended',
      apps: [
        { id: 'settings', name: 'Settings', icon: 'cog', component: 'Settings' },
        { id: 'web-browser', name: 'Web Browser', icon: 'globe', component: 'WebBrowser', url: 'duckduckgo://search' },
        { id: 'store', name: 'App Store', icon: 'store', component: 'Store' }
      ]
    }
  ];

  const handleAppClick = (app) => {
    console.log("Start Menu app clicked:", app);
    
    // Create a new app instance with a unique ID
    const appToLaunch = {
      ...app,
      id: `${app.id}-${Date.now()}`
    };
    
    console.log("Launching app from Start Menu:", appToLaunch);
    launchApp(appToLaunch);
    closeMenu();
  };

  // Get current section based on active category
  const getCurrentSection = () => {
    const sectionIndex = categories.findIndex(cat => cat.id === activeCategory);
    return sectionIndex >= 0 ? appSections[sectionIndex] : appSections[0];
  };

  // Handle system restart - clears localStorage and reloads the page
  const handleRestart = () => {
    // Clear all localStorage
    localStorage.clear();
    // Reload the page to get a fresh start
    window.location.reload();
  };

  return (
    <div 
      className="start-menu glass-effect"
      style={{
        width: '800px',
        backgroundColor: 'rgba(20, 20, 30, 0.8)',
        boxShadow: '0 12px 44px rgba(0, 0, 0, 0.5)',
        borderRadius: '28px',
        padding: '24px',
        color: 'white',
        overflow: 'hidden',
        backdropFilter: 'blur(30px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        position: 'relative'
      }}
      ref={menuRef}
    >
      {/* Restart Confirmation Popup */}
      {showRestartConfirm && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(10px)',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '28px'
        }}>
          <div style={{
            backgroundColor: 'rgba(40, 40, 60, 0.95)',
            padding: '25px',
            borderRadius: '20px',
            maxWidth: '400px',
            textAlign: 'center',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{ fontSize: '20px', marginBottom: '5px', fontWeight: 'bold' }}>
              Restart WebOS
            </div>
            <div style={{ fontSize: '14px', marginBottom: '25px', opacity: 0.8, lineHeight: '1.5' }}>
              This will clear all your data and restart WebOS to its initial state. Are you sure you want to continue?
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
              <button
                onClick={() => setShowRestartConfirm(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  borderRadius: '10px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              >
                Cancel
              </button>
              <button
                onClick={handleRestart}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'rgba(235, 87, 87, 0.7)',
                  border: 'none',
                  borderRadius: '10px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(235, 87, 87, 0.9)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(235, 87, 87, 0.7)'}
              >
                Restart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Categories navbar */}
      <div className="start-categories" style={{ 
        display: 'flex',
        justifyContent: 'center',
        gap: '20px',
        marginBottom: '30px'
      }}>
        {categories.map(category => (
          <div 
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              opacity: category.id === activeCategory ? 1 : 0.7,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.opacity = 1;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.opacity = category.id === activeCategory ? 1 : 0.7;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: category.id === activeCategory ? 'rgba(255, 74, 163, 0.2)' : 'rgba(255, 255, 255, 0.1)',
              color: category.id === activeCategory ? '#ff4aa3' : 'white',
            }}>
              <i className={`fas fa-${category.icon}`} style={{ fontSize: '16px' }}></i>
            </div>
            <div style={{ fontSize: '11px' }}>{category.label}</div>
            {category.id === activeCategory && (
              <div style={{
                width: '20px',
                height: '3px',
                backgroundColor: '#ff4aa3',
                borderRadius: '2px',
                marginTop: '-2px'
              }}></div>
            )}
          </div>
        ))}
      </div>
      
      {/* Search bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '16px',
        padding: '0 15px',
        height: '40px',
        marginBottom: '20px'
      }}>
        <i className="fas fa-search" style={{ color: 'white', opacity: 0.7, marginRight: '10px' }}></i>
        <input 
          type="text" 
          placeholder="Type here to search" 
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: 'white',
            outline: 'none',
            width: '100%',
            fontSize: '14px'
          }}
        />
      </div>
      
      <div className="app-section" style={{ marginBottom: '20px' }}>
        <div className="section-header" style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          marginBottom: '15px',
          alignItems: 'center' 
        }}>
          <div style={{ fontSize: '15px', fontWeight: '500' }}>
            {getCurrentSection().title}
          </div>
          {activeCategory !== 'all' && (
            <div 
              style={{ 
                fontSize: '13px', 
                opacity: 0.7,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
              onClick={() => setActiveCategory('all')}
            >
              <span>All apps</span>
              <i className="fas fa-chevron-right" style={{ fontSize: '10px' }}></i>
            </div>
          )}
        </div>
        
        <div className="app-grid" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(6, 1fr)', 
          gap: '15px',
          width: '100%'
        }}>
          {getCurrentSection().apps.map(app => (
            <div 
              key={app.id}
              className="app-item"
              onClick={() => handleAppClick(app)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '15px 5px',
                cursor: 'pointer',
                borderRadius: '16px',
                transition: 'all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 7px 20px rgba(0, 0, 0, 0.2)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div 
                style={{
                  width: '50px',
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '8px',
                  borderRadius: '14px',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))',
                  backdropFilter: 'blur(5px)',
                  transition: 'transform 0.2s ease'
                }}
                className="app-icon"
              >
                <i className={app.className ? `${app.className} fa-${app.icon}` : `fas fa-${app.icon}`} style={{ 
                  fontSize: '22px',
                  color: 'white'
                }}></i>
              </div>
              <div style={{ 
                fontSize: '12px', 
                textAlign: 'center', 
                fontWeight: '400',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {app.name}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="start-footer" style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '15px 0 0 0',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div 
          className="user-profile"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '8px 12px',
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            backgroundColor: 'rgba(255, 255, 255, 0.05)'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)'}
        >
          <div style={{ 
            width: '36px', 
            height: '36px', 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, #ff4aa3, #915eff)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="fas fa-user" style={{ fontSize: '16px', color: 'white' }}></i>
          </div>
          <div>
            <div style={{ fontWeight: '500', fontSize: '14px' }}>User</div>
            <div style={{ fontSize: '11px', opacity: '0.7' }}>Local Account</div>
          </div>
        </div>
        
        <div className="quick-actions">
          <div 
            className="restart-button"
            onClick={() => setShowRestartConfirm(true)}
            style={{ 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              backgroundColor: 'rgba(255, 74, 74, 0.2)',
              color: '#ff4a4a'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 74, 74, 0.3)'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 74, 74, 0.2)'}
            title="Restart WebOS"
          >
            <i className="fas fa-redo-alt"></i>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartMenu; 